import React, { useMemo } from 'react'
import styled from 'styled-components'
import { format } from 'date-fns-tz'

/**
 * PointRecord
 * - no
 * - type
 * - operation
 * - point
 * - description
 * - record_date // iso string
 */
export const PointHistory = React.memo(function ({ pointRecords }) {
  const groupedPoints = useMemo(
    () =>
      pointRecords.reduce((prev, { no, type, operation, point, description, record_date }) => {
        const krFormattedDate = format(new Date(record_date), 'yyyy-MM-dd', 'Asia/Seoul')

        if (prev.hasOwnProperty(krFormattedDate)) {
          return {
            ...prev,
            [krFormattedDate]: [
              ...prev[krFormattedDate],
              {
                no,
                type,
                operation,
                point,
                description,
              },
            ],
          }
        }

        return {
          ...prev,
          [krFormattedDate]: [
            {
              no,
              type,
              operation,
              point,
              description,
            },
          ],
        }
      }, {}),
    [pointRecords],
  )

  return (
    <PointHistoryContainer>
      {Object.entries(groupedPoints).map(([date, points]) => {
        const dateObj = new Date(date) // WARN: utc 기준이기는 하지만 날짜까지만 사용할 것이기 때문에 문제 없음
        const year = dateObj.getFullYear()
        const month = dateObj.getMonth() + 1
        const day = dateObj.getDate()

        const thisYear = new Date().getFullYear()

        const formattedDatePartList = [
          <PointHistoryDatePart key={'month'}>{`${month
            .toString()
            .padStart(2, '0')}.`}</PointHistoryDatePart>,
          <PointHistoryDatePart key={'date'}>{`${day
            .toString()
            .padStart(2, '0')}`}</PointHistoryDatePart>,
        ]

        if (year < thisYear) {
          formattedDatePartList.unshift(
            <PointHistoryDatePart key={'year'}>{`${year
              .toString()
              .slice(2)}.`}</PointHistoryDatePart>,
          )
        }

        return (
          <GroupedPointHistoryItem key={date}>
            <PointHistoryDate>{formattedDatePartList}</PointHistoryDate>

            <PointHistoryItemList>
              {points.map(({ no, description, operation, point }) => (
                <PointHistoryItem key={no}>
                  <PointHistoryDesc>
                    {description || `포인트 ${operation === 'add' ? '획득' : '차감'}`}
                  </PointHistoryDesc>
                  <PointHistoryAmount operation={operation}>{`${
                    operation === 'add' ? '+' : ''
                  }${new Intl.NumberFormat().format(point)}P`}</PointHistoryAmount>
                </PointHistoryItem>
              ))}
            </PointHistoryItemList>
          </GroupedPointHistoryItem>
        )
      })}
    </PointHistoryContainer>
  )
})

const PointHistoryContainer = styled.div`
  padding: 0 calc(100vw * (17 / 375));
  margin-bottom: calc(100vw * (44 / 375));
`

const GroupedPointHistoryItem = styled.div`
  padding-top: calc(100vw * (23 / 375));
  padding-bottom: calc(100vw * (28 / 375));
  border-bottom: 1px solid #e6e6e6;

  &:last-child {
    border-bottom: none;
  }
`

const PointHistoryDate = styled.div`
  margin-bottom: calc(100vw * (20 / 375));
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PointHistoryDatePart = styled.div`
  font-weight: 600;
  font-size: calc(100vw * (16 / 375));
  line-height: calc(100vw * (23 / 375));
  color: #989898;
  margin-right: calc(100vw * (2 / 375));

  &:last-child {
    margin-right: 0;
  }
`

const PointHistoryItemList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const PointHistoryItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: calc(100vw * (28 / 375));

  &:last-child {
    margin-bottom: 0;
  }
`

const PointHistoryDesc = styled.div`
  font-weight: 500;
  font-size: calc(100vw * (16 / 375));
  line-height: calc(100vw * (19 / 375));
  color: #121212;
  max-width: calc(100vw * (220 / 375));
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const PointHistoryAmount = styled.div`
  font-weight: 600;
  font-size: calc(100vw * (18 / 375));
  line-height: calc(100vw * (23 / 375));
  color: ${({ operation }) => (operation === 'add' ? '#5191F1' : '#E04B37')};
  max-width: calc(100vw * (121 / 375));
  word-break: break-all;
`
